// iter117 Session 3 — Page Prise en charge "Sur place" (desktop, admin)
// Single-page layout (pas de stepper) : client + véhicule + photos + remarques visibles en même temps.
// Utilise les mêmes endpoints publics que la version mobile (token = capability).
// Option : afficher un QR pour que le receveur prenne les photos depuis son téléphone (même session).
// iter166 — Ajout auto-complétion NPA → Localité (cohérence UX avec ClientForm/QuickClientDialog).
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Loader2, Camera, CheckCircle2, QrCode, X, ArrowLeft } from "lucide-react";
import { useNpaLookup } from "@/lib/useNpaLookup";
import { npaPlaceholder } from "@/lib/npa";

const PUBLIC = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const t = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function Input({ label, value, onChange, required, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-700">{label}{required && <span className="text-red-500"> *</span>}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-0.5 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-[#52a675] focus:ring-2 focus:ring-[#52a675]/30 focus:outline-none"
      />
    </label>
  );
}

export default function PriseEnChargeSurPlace() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [client, setClient] = useState({});
  const [vehicule, setVehicule] = useState({});
  const [remarques, setRemarques] = useState("");
  const [carteGrise, setCarteGrise] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showQrPhotos, setShowQrPhotos] = useState(false);

  const cgInputRef = useRef(null);
  const photoInputRef = useRef(null);

  // iter166 — Auto-complétion Localité selon (NPA, Pays). L'input reste libre (datalist).
  const clientPays = client.pays || "CH";
  const { options: localiteOptions, loading: localiteLoading } = useNpaLookup(
    client.code_postal,
    clientPays,
    client.ville,
    (name) => setClient((c) => ({ ...c, ville: name })),
  );

  useEffect(() => {
    axios.get(`${PUBLIC}/prise-en-charge/${token}`)
      .then(({ data }) => {
        setSession(data);
        setClient(data.client_draft || {});
        setVehicule(data.vehicule_draft || {});
        setRemarques(data.remarques || "");
        setCarteGrise(data.carte_grise_photo || null);
        setPhotos(data.photos_vehicule || []);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Session inconnue ou expirée"))
      .finally(() => setLoading(false));
  }, [token]);

  // Polling auto si QR photos ouvert (pour récupérer photos prises depuis le mobile)
  useEffect(() => {
    if (!showQrPhotos) return undefined;
    const id = setInterval(() => {
      axios.get(`${PUBLIC}/prise-en-charge/${token}`)
        .then(({ data }) => {
          setPhotos(data.photos_vehicule || []);
          setCarteGrise(data.carte_grise_photo || null);
        })
        .catch(() => {});
    }, 4000);
    return () => clearInterval(id);
  }, [showQrPhotos, token]);

  const uploadFile = async (file, kind) => {
    const form = new FormData();
    form.append("file", file);
    const url = `${PUBLIC}/prise-en-charge/${token}/photo/${kind === "carte_grise" ? "carte-grise" : "vehicule"}`;
    const { data } = await axios.post(url, form, { headers: { "Content-Type": "multipart/form-data" } });
    // iter130 — Pré-remplissage auto via OCR si carte grise détectée
    if (kind === "carte_grise" && data.ocr_fields) {
      const f = data.ocr_fields;
      const confiance = parseFloat(f.confiance || 0);
      if (confiance >= 0.3) {
        setVehicule((v) => ({
          ...v,
          marque: v.marque || f.marque || "",
          modele: v.modele || f.modele || "",
          plaque: v.plaque || f.plaque || "",
          vin: v.vin || f.vin || "",
          premiere_mec: v.premiere_mec || f.premiere_mec || "",
        }));
        setClient((c) => ({
          ...c,
          nom: c.nom || f.titulaire_nom || "",
          prenom: c.prenom || f.titulaire_prenom || "",
          rue: c.rue || f.titulaire_rue || "",
          code_postal: c.code_postal || f.titulaire_npa || "",
          ville: c.ville || f.titulaire_ville || "",
        }));
        toast.success("Champs pré-remplis depuis la carte grise — vérifie et complète.");
      } else {
        toast.info("Carte grise peu lisible — saisis manuellement.");
      }
    }
    return data.path;
  };

  const handleCarteGrise = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      toast.loading("Lecture de la carte grise...", { id: "cg-ocr" });
      const p = await uploadFile(f, "carte_grise");
      setCarteGrise(p);
      toast.dismiss("cg-ocr");
    }
    catch (err) {
      toast.dismiss("cg-ocr");
      toast.error(err.response?.data?.detail || "Erreur upload");
    }
  };

  const handlePhotoVehicule = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (photos.length >= 10) { toast.error("Maximum 10 photos atteint"); return; }
    try { const p = await uploadFile(f, "vehicule"); setPhotos([...photos, p]); toast.success("Photo ajoutée"); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); }
  };

  const validateAll = () => {
    // iter128 — Si carte grise photographiée, seuls téléphone et km restent obligatoires
    // iter157 — Si client entreprise, raison_sociale au lieu de nom+prenom
    const isEntreprise = client.type === "entreprise";
    let reqClient;
    if (isEntreprise) {
      reqClient = carteGrise ? ["raison_sociale", "tel_principal"] : ["raison_sociale", "rue", "code_postal", "ville", "tel_principal"];
    } else {
      reqClient = carteGrise ? ["nom", "prenom", "tel_principal"] : ["nom", "prenom", "rue", "code_postal", "ville", "tel_principal"];
    }
    const missingC = reqClient.filter((k) => !(client[k] || "").toString().trim());
    if (missingC.length) { toast.error(`Client — manquants : ${missingC.join(", ")}`); return false; }
    // iter279 — L'OR est créé dans la foulée : marque/modèle/plaque/VIN sont
    // donc toujours requis (pré-remplis par l'OCR de la carte grise).
    const reqVeh = carteGrise ? ["marque", "modele", "plaque", "vin", "km"] : ["marque", "modele", "premiere_mec", "plaque", "vin", "km"];
    const missingV = reqVeh.filter((k) => !(vehicule[k] || "").toString().trim());
    if (missingV.length) { toast.error(`Véhicule — manquants : ${missingV.join(", ")}`); return false; }
    return true;
  };

  const handleFinish = async () => {
    if (!validateAll()) return;
    setSubmitting(true);
    try {
      await axios.put(`${PUBLIC}/prise-en-charge/${token}/client`, client);
      await axios.put(`${PUBLIC}/prise-en-charge/${token}/vehicule`, { ...vehicule, km: parseInt(vehicule.km) || 0 });
      await axios.put(`${PUBLIC}/prise-en-charge/${token}/remarques`, { remarques });
      await axios.post(`${PUBLIC}/prise-en-charge/${token}/finish`);
      // iter279 — On enchaîne directement sur la création de l'OR : plus besoin
      // de repasser par le calendrier pour cliquer "Démarrer l'OR".
      const { data } = await axios.post(
        `${API}/prise-en-charge/${token}/create-or`,
        { client, vehicule: { ...vehicule, km: parseInt(vehicule.km) || 0 }, remarques },
        { headers: authHeaders() },
      );
      toast.success(`Prise en charge terminée — OR ${data.or_numero} créé`);
      navigate(`/atelier/or/${data.or_id}`);
    } catch (e) {
      const detail = e.response?.data?.detail || "Erreur";
      if (e.config?.url?.includes("/create-or")) {
        toast.error(`Prise en charge enregistrée, mais OR non créé : ${detail}. Complète les champs véhicule puis réessaie.`);
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-10 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#52a675]" /></div>;
  if (!session) return <div className="p-10 text-center text-gray-600">Session inconnue ou expirée.</div>;

  const mobileUrl = `${window.location.origin}/prise-en-charge/${token}`;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="pec-surplace-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/calendrier")} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="pec-sp-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Prise en charge — Sur place</h1>
            <p className="text-xs text-gray-500">Vérifie les informations : l&apos;OR est créé automatiquement à la fin.</p>
          </div>
        </div>
        <button
          onClick={() => setShowQrPhotos(true)}
          className="px-3 py-2 bg-white border border-[#52a675] text-[#52a675] rounded-lg text-sm font-medium hover:bg-[#52a675]/10 flex items-center gap-2"
          data-testid="pec-sp-qr-photos"
        >
          <QrCode className="w-4 h-4" /> Photos depuis mobile
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Client */}
        <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Client</h2>
            <div className="flex items-center gap-2">
              {client.type === "entreprise" && (
                <span className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded" data-testid="pec-sp-client-entreprise-badge">Entreprise</span>
              )}
              {carteGrise && <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded" data-testid="pec-sp-client-cg-info">📷 Carte grise jointe — adresse facultative</span>}
              {(session.client_draft?.nom || session.client_draft?.raison_sociale) && <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Pré-rempli</span>}
            </div>
          </div>
          {client.type === "entreprise" ? (
            <div>
              <Input label="Raison sociale" value={client.raison_sociale} onChange={(v) => setClient({ ...client, raison_sociale: v })} required testid="pec-sp-client-raison-sociale" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Input label="Nom" value={client.nom} onChange={(v) => setClient({ ...client, nom: v })} required testid="pec-sp-client-nom" />
              <Input label="Prénom" value={client.prenom} onChange={(v) => setClient({ ...client, prenom: v })} required testid="pec-sp-client-prenom" />
            </div>
          )}
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-3"><Input label="Rue" value={client.rue} onChange={(v) => setClient({ ...client, rue: v })} required={!carteGrise} testid="pec-sp-client-rue" /></div>
            <Input label="N°" value={client.numero} onChange={(v) => setClient({ ...client, numero: v })} testid="pec-sp-client-numero" />
          </div>
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-1">
              <label className="block">
                <span className="text-xs font-medium text-gray-700">NPA{!carteGrise && <span className="text-red-500"> *</span>}</span>
                <input
                  type="text"
                  value={client.code_postal || ""}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^A-Za-z0-9 \-]/g, "").slice(0, 10);
                    setClient({ ...client, code_postal: v });
                  }}
                  maxLength={10}
                  placeholder={npaPlaceholder(clientPays)}
                  data-testid="pec-sp-client-npa"
                  className="mt-0.5 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-[#52a675] focus:ring-2 focus:ring-[#52a675]/30 focus:outline-none"
                />
              </label>
            </div>
            <div className="col-span-3">
              <label className="block">
                <span className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                  Ville{!carteGrise && <span className="text-red-500"> *</span>}
                  {localiteLoading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
                  {!localiteLoading && localiteOptions.length > 0 && (
                    <span className="text-[10px] text-emerald-600 font-normal">
                      {localiteOptions.length === 1 ? "auto-complété" : `${localiteOptions.length} choix`}
                    </span>
                  )}
                </span>
                <input
                  type="text"
                  value={client.ville || ""}
                  onChange={(e) => setClient({ ...client, ville: e.target.value })}
                  list="pec-sp-loc-options"
                  data-testid="pec-sp-client-ville"
                  className="mt-0.5 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-[#52a675] focus:ring-2 focus:ring-[#52a675]/30 focus:outline-none"
                />
                <datalist id="pec-sp-loc-options">
                  {localiteOptions.map((opt, i) => (
                    <option key={i} value={opt.name}>{opt.canton ? `${opt.name} (${opt.canton})` : opt.name}</option>
                  ))}
                </datalist>
              </label>
            </div>
            <div className="col-span-2">
              <label className="block">
                <span className="text-xs font-medium text-gray-700">Pays</span>
                <select
                  value={clientPays}
                  onChange={(e) => setClient({ ...client, pays: e.target.value })}
                  data-testid="pec-sp-client-pays"
                  className="mt-0.5 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:border-[#52a675] focus:ring-2 focus:ring-[#52a675]/30 focus:outline-none"
                >
                  <option value="CH">🇨🇭 Suisse</option>
                  <option value="FR">🇫🇷 France</option>
                  <option value="DE">🇩🇪 Allemagne</option>
                  <option value="IT">🇮🇹 Italie</option>
                  <option value="AT">🇦🇹 Autriche</option>
                  <option value="LI">🇱🇮 Liechtenstein</option>
                  <option value="">Autre</option>
                </select>
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Téléphone" value={client.tel_principal} onChange={(v) => setClient({ ...client, tel_principal: v })} required type="tel" testid="pec-sp-client-tel" />
            <Input label="Email" value={client.email} onChange={(v) => setClient({ ...client, email: v })} type="email" testid="pec-sp-client-email" />
          </div>
        </section>

        {/* Véhicule */}
        <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Véhicule</h2>
            <div className="flex items-center gap-2">
              {carteGrise && <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded" data-testid="pec-sp-veh-cg-info">📷 Carte grise jointe — 1ère mise en circ. facultative</span>}
              {session.vehicule_draft?.marque && <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Pré-rempli</span>}
            </div>
          </div>
          {/* Carte grise photo */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-xs text-blue-900 mb-2 font-medium">📷 Pour aller plus vite : envoie la photo de la carte grise — les autres champs deviendront facultatifs (OCR utilisé plus tard pour l&apos;OR).</div>
            <input ref={cgInputRef} type="file" accept="image/*" className="hidden" onChange={handleCarteGrise} />
            <button onClick={() => cgInputRef.current?.click()} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium" data-testid="pec-sp-cg-upload">
              {carteGrise ? "✓ Carte grise envoyée — Remplacer" : "Sélectionner un fichier"}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Marque" value={vehicule.marque} onChange={(v) => setVehicule({ ...vehicule, marque: v })} required testid="pec-sp-veh-marque" />
            <Input label="Type / Modèle" value={vehicule.modele} onChange={(v) => setVehicule({ ...vehicule, modele: v })} required testid="pec-sp-veh-modele" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="1ère mise en circulation" value={vehicule.premiere_mec} onChange={(v) => setVehicule({ ...vehicule, premiere_mec: v })} required={!carteGrise} type="date" testid="pec-sp-veh-mec" />
            <Input label="Kilométrage" value={vehicule.km} onChange={(v) => setVehicule({ ...vehicule, km: v.replace(/\D/g, "") })} required type="tel" testid="pec-sp-veh-km" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Plaque" value={vehicule.plaque} onChange={(v) => setVehicule({ ...vehicule, plaque: v.toUpperCase() })} required testid="pec-sp-veh-plaque" />
            <Input label="N° châssis (VIN)" value={vehicule.vin} onChange={(v) => setVehicule({ ...vehicule, vin: v.toUpperCase().replace(/[^A-Z0-9]/g, "") })} required testid="pec-sp-veh-vin" />
          </div>
        </section>

        {/* Photos véhicule */}
        <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3 lg:col-span-1">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Photos du véhicule</h2>
            <span className="text-xs text-gray-500">{photos.length}/10</span>
          </div>
          <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoVehicule} />
          <button onClick={() => photoInputRef.current?.click()} className="w-full px-4 py-3 bg-[#52a675] text-white rounded-xl font-medium flex items-center justify-center gap-2" data-testid="pec-sp-photo-add">
            <Camera className="w-5 h-5" /> Ajouter une photo
          </button>
          <div className="grid grid-cols-4 gap-2">
            {photos.map((p, i) => (
              <div key={i} className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center relative">
                <Camera className="w-6 h-6 text-gray-400" />
                <span className="absolute bottom-1 right-1 bg-white text-[10px] px-1 rounded">{i + 1}</span>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500">Recommandé : 4 angles. Astuce : utilise le bouton &quot;Photos depuis mobile&quot; pour scanner avec un téléphone.</div>
        </section>

        {/* Remarques */}
        <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3 lg:col-span-1">
          <h2 className="font-semibold text-gray-900">Remarques du client</h2>
          <textarea
            value={remarques}
            onChange={(e) => setRemarques(e.target.value)}
            rows={8}
            data-testid="pec-sp-remarques"
            placeholder="Bruit en virage à gauche, voyant moteur allumé depuis lundi, fuite d'huile constatée…"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-[#52a675] focus:ring-2 focus:ring-[#52a675]/30 focus:outline-none"
          />
        </section>
      </div>

      {/* Footer action */}
      <div className="sticky bottom-4 bg-white/80 backdrop-blur border border-gray-200 rounded-2xl p-4 flex items-center justify-between shadow-lg">
        <div className="text-xs text-gray-500">
          {showQrPhotos && <span className="inline-flex items-center gap-1 text-[#52a675]"><Loader2 className="w-3 h-3 animate-spin" /> Sync mobile actif</span>}
        </div>
        <button
          onClick={handleFinish}
          disabled={submitting}
          className="px-6 py-3 bg-[#52a675] text-white rounded-xl font-semibold hover:bg-[#3d8a5c] disabled:opacity-50 flex items-center gap-2"
          data-testid="pec-sp-finish"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Terminer et créer l&apos;OR
        </button>
      </div>

      {/* QR photos mobile modal */}
      {showQrPhotos && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowQrPhotos(false)} data-testid="pec-sp-qr-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h2 className="text-lg font-bold text-gray-900">📱 Photos depuis le mobile</h2>
                <p className="text-sm text-gray-600">Scanne ce QR avec le téléphone du receveur. Les photos arrivent automatiquement ici.</p>
              </div>
              <button onClick={() => setShowQrPhotos(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="bg-white border-2 border-gray-100 rounded-xl p-4 flex items-center justify-center">
              <QRCodeCanvas value={mobileUrl} size={220} level="M" />
            </div>
            <div className="text-[11px] text-gray-500 mt-2 text-center break-all">{mobileUrl}</div>
            <button onClick={() => setShowQrPhotos(false)} className="mt-3 w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium" data-testid="pec-sp-qr-close">
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
